import React, { useEffect, useState, useRef, useMemo } from "react";
import "./CartPage.css";
import { useCart, cartItemsKey } from "../context/CartContext";
import { cacheGet, cacheSet, cacheClear } from "../utils/sessionCache";
import { useAuth } from "../context/AuthContext";
import { formatRupees } from "../utils/money";
import { CoinIcon } from "./RewardCard";
import RewardsPage from "./RewardsPage";
import OrderNote from "./OrderNote";
import CircleStatusRow from "./CircleStatusRow";
import JoinCircleSheet from "./JoinCircleSheet";
import TablePicker from "./TablePicker";
import { fetchCafeTables } from "../api";
import useCafeMenuCheckout from "../hooks/useCafeMenuCheckout";
import { PayingOverlay, PaymentIssueScreen, OrderPlacedScreen } from "./CheckoutScreens";
import {
  getLoyaltyPoints,
  getCircleStatus,
  getCircleRewards,
  saveCafeMenuCart,
  toCafeMenuApiItems,
  totalsOf,
  applyCafeMenuReward,
  removeCafeMenuReward,
  CafeMenuApiError,
  CafeMenuMultiDeviceError,
} from "../cafemenuApi";
import {
  trackCartPhoneCaptured,
  trackCartPhoneCompleted,
  trackCartStepViewed,
  trackCartTableCompleted,
  trackCartAbandoned,
} from "../utils/analytics";

// First word of the account name for a greeting. Accounts created through the QR menu are named "Guest",
// which reads oddly in "Guest, you're eligible" — treat that as no name.
const firstNameOf = (fullName) => {
  const first = String(fullName || "").trim().split(/\s+/)[0];
  return /^guest$/i.test(first) ? "" : first;
};

const isValidIndianPhone = (value) => {
  const digits = value.replace(/\D/g, "");
  if (digits.startsWith("91") && digits.length === 12) return true;
  if (digits.length === 10 && /^[6-9]/.test(digits)) return true;
  return false;
};

const EditIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.5 1.5l2 2M1 11l.7-2.8L9.2 .7a1 1 0 011.4 0l.7.7a1 1 0 010 1.4L3.8 10.3 1 11z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const NEW_TAG_ID = "6932636dec4624b7afca0266";

// Dietary indicators (veg / non-veg / egg / spicy) for a cart item.
const renderDietaryTags = (item) => {
  const tags = Array.isArray(item.tags) ? item.tags : [];
  if (tags.length === 0) return null;
  const hasEgg = tags.some((t) => t?.name?.toLowerCase().includes("egg"));
  const hasNonVeg = tags.some((t) => {
    const n = t?.name?.toLowerCase() || "";
    return n.includes("non") && n.includes("veg");
  });
  const icons = [];
  if (hasEgg) {
    icons.push(<img key="egg" src="/EggTag.png" alt="Egg" className="cart-row-diet-egg" />);
  }
  tags.forEach((t, i) => {
    const name = t?.name?.toLowerCase() || "";
    if (t?.id === NEW_TAG_ID || name.includes("new")) return;
    if (hasEgg && name.includes("egg")) return;
    if (hasEgg && hasNonVeg && name.includes("non")) return;
    if (t?.tagIcon?.fullPath) {
      icons.push(<img key={t.id || i} src={t.tagIcon.fullPath} alt={t.name || ""} className="cart-row-diet-icon" />);
    }
  });
  if (icons.length === 0) return null;
  return <span className="cart-row-diet-tags">{icons}</span>;
};

// Percent-tag glyph for the Offers button.
const OfferIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="cart-offer-icon">
    <path d="M3.5 12.2V5.6a2.1 2.1 0 0 1 2.1-2.1h6.6l8.3 8.3a2 2 0 0 1 0 2.8l-6.6 6.6a2 2 0 0 1-2.8 0l-7.6-7.6a2 2 0 0 1-.9-1.4Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
    <circle cx="8" cy="8" r="1.4" fill="currentColor" />
    <path d="M10.2 15.3l4.6-4.6M10.6 11.1h.01M14.4 14.9h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

export default function CartPage({ cafeId, cafeName, onBack, onEditItem, onViewSummary, onOrderDone }) {
  const {
    items,
    itemCount,
    subtotal,
    taxTotal,
    grandTotal,
    updateQuantity,
    removeItem,
    phoneNumber,
    setPhoneNumber,
    tableNumber,
    setTableNumber,
    tableName,
    setTableName,
    orderType,
    setOrderType,
    specialInstructions,
    setSpecialInstructions,
    appliedDiscount,
    setAppliedDiscount,
    appliedReward,
    setAppliedReward,
    serverTotals,
    setServerTotals,
    getApiTaxRate,
    getSavedCart,
    claimCartSave,
    finishCartSave,
    resetSavedCart,
    cafeMenuCartId,
    setCafeMenuCartId,
    clearCart,
  } = useCart();
  const {
    isAuthenticated,
    token,
    userId,
    phoneNumber: authPhoneNumber,
    name: authName,
    reauthRequired,
    requestReauth,
    cancelReauth,
    isRecentlyVerified,
    logout,
    sendOtp,
    verifyOtp,
  } = useAuth();

  // The Rewards page (opened from the "Rewards" line) sits above the cart.
  const [showRewards, setShowRewards] = useState(false);
  // Joining Circle happens in a sheet over the cart; what the verify step is asking for again, if anything.
  const [showJoin, setShowJoin] = useState(false);
  const [reauthFor, setReauthFor] = useState("rewards");
  // The sheet belongs to this cart and this login: if the cart empties (e.g. it timed out while the customer
  // was away) or the login ends, it must not stay hanging over the empty cart.
  useEffect(() => {
    if (showJoin && (items.length === 0 || !isAuthenticated)) setShowJoin(false);
  }, [showJoin, items.length, isAuthenticated]);
  // Pay straight from the cart: payment, order confirmation and the result screens live here.
  const checkout = useCafeMenuCheckout({ cafeId, cafeName });
  // Bill details are collapsed by default so the items keep most of the screen.
  const [billOpen, setBillOpen] = useState(false);
  // Peeking at the Verify step while already verified (stepper tap).
  const [verifyPeek, setVerifyPeek] = useState(false);
  const [step, setStep] = useState(() => {
    // A returning customer who already settled step 0 (table chosen AND dining
    // type picked) lands past it.
    if (tableNumber.trim().length > 0 && orderType) return 1;
    return 0;
  });
  const [returnStep, setReturnStep] = useState(null);
  const [tableError, setTableError] = useState(false);
  // Shown if they tap Continue on step 0 without picking Dine-in / Takeaway.
  const [orderTypeError, setOrderTypeError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);

  // OTP sub-flow within step 1.
  const [otpStage, setOtpStage] = useState("phone"); // "phone" | "otp"
  const [otpCode, setOtpCode] = useState("");
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpMessage, setOtpMessage] = useState("");
  const [multiDeviceConfirm, setMultiDeviceConfirm] = useState(null);

  // Rewards step (step 2): Circle Rewards only — offers are applied, never typed in.
  const [cartSyncError, setCartSyncError] = useState("");

  // Circle membership gate: rewards are for members only; everyone else sees
  // their progress toward membership (same data/wording as the app's profile).
  const [circle, setCircle] = useState(() => cacheGet(`circle:${token}`) || null);
  const [circleLoading, setCircleLoading] = useState(false);
  const [circleError, setCircleError] = useState("");
  const [circleRetry, setCircleRetry] = useState(0);

  const [loyaltyBalance, setLoyaltyBalance] = useState(() => {
    const cached = cacheGet(`coins:${token}`);
    return cached === undefined ? null : cached;
  });
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  const [loyaltyError, setLoyaltyError] = useState("");

  const [rewardOffers, setRewardOffers] = useState([]);
  const [rewardOffersLoading, setRewardOffersLoading] = useState(false);
  const [rewardOffersError, setRewardOffersError] = useState("");
  // The cache key the current rewardOffers belong to. Until it matches the key
  // for the cart on screen, the rewards are still being prepared (waiting for
  // the cart to save, or the request in flight) and the Rewards page shows a
  // loader instead of a premature "No rewards available".
  const [rewardOffersKey, setRewardOffersKey] = useState("");
  const [rewardApplyingId, setRewardApplyingId] = useState(null);
  const [rewardApplyError, setRewardApplyError] = useState("");

  // The cafe's tables for the picker. Loaded once when the cart opens (a customer action, never in the
  // background) and reused for the visit — also when the cart opens past the Table step with a table saved
  // from before, so that table can be checked. "empty" = the cafe has none set up -> typing the number is the fallback.
  const TABLES_TTL_MS = 30 * 60 * 1000;
  const [tables, setTables] = useState(() => {
    const cached = cacheGet(`tables:${cafeId}`);
    return cached === undefined ? { status: "idle", list: [] } : { status: cached.length ? "ready" : "empty", list: cached };
  });
  const [tablesAttempt, setTablesAttempt] = useState(0);
  const manualTable = tables.status === "empty";

  const tableRef = useRef(null);
  const phoneRef = useRef(null);
  const otpRef = useRef(null);
  const viewedStepsRef = useRef(new Set());

  const cartPageRef = useRef(null);
  // True while the on-screen keyboard is up (visual viewport much shorter than
  // the layout viewport). Used to collapse the cart list and give the form room.
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  // True while the "note for the barista" editor is open (it needs the whole screen above the keyboard).
  const [noteEditing, setNoteEditing] = useState(false);

  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";

    // Keyboard handling: on Android, interactive-widget=resizes-content shrinks
    // the layout viewport so the fixed panel sits above the keyboard on its own.
    // iOS Safari ignores that descriptor and instead scrolls the *visual*
    // viewport down (offsetTop > 0) to reveal the focused input — a panel pinned
    // at top:0 then slides half off-screen and the menu shows through beneath it.
    // So we pin the panel to the visual viewport's top AND height, and flag the
    // keyboard as open so the form can take over the whole visible area.
    const vv = window.visualViewport;
    let frame = 0;
    const applyViewport = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const el = cartPageRef.current;
        if (!el || !vv) return;
        el.style.height = `${vv.height}px`;
        el.style.top = `${Math.max(0, vv.offsetTop)}px`;
        setKeyboardOpen(window.innerHeight - vv.height > 150);
      });
    };

    if (vv) {
      vv.addEventListener("resize", applyViewport);
      vv.addEventListener("scroll", applyViewport);
      applyViewport();
    }

    return () => {
      cancelAnimationFrame(frame);
      if (vv) {
        vv.removeEventListener("resize", applyViewport);
        vv.removeEventListener("scroll", applyViewport);
      }
      const el = cartPageRef.current;
      if (el) { el.style.height = ""; el.style.top = ""; }
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      window.scrollTo(0, scrollY);
    };
  }, []);

  const hasItems = items.length > 0;
  useEffect(() => {
    if (!hasItems || !cafeId) return undefined;
    const cached = cacheGet(`tables:${cafeId}`);
    if (cached !== undefined) {
      setTables({ status: cached.length ? "ready" : "empty", list: cached });
      return undefined;
    }
    let cancelled = false;
    setTables({ status: "loading", list: [] });
    fetchCafeTables(cafeId)
      .then((list) => {
        if (cancelled) return;
        cacheSet(`tables:${cafeId}`, list, TABLES_TTL_MS);
        setTables({ status: list.length ? "ready" : "empty", list });
      })
      .catch(() => { if (!cancelled) setTables({ status: "error", list: [] }); });
    return () => { cancelled = true; };
  }, [hasItems, cafeId, tablesAttempt, TABLES_TTL_MS]);

  // A table remembered from before that this cafe doesn't have (e.g. another cafe's) is not a choice; one it does
  // have gets its exact POS name (a table picked before the name was kept, or renamed since).
  useEffect(() => {
    if (tables.status !== "ready" || !tableNumber) return;
    const known = tables.list.find((t) => t.number === tableNumber);
    if (!known) {
      setTableNumber("");
      setTableName("");
      // Already past the Table step with that table: ask for it again.
      if (step > 0) { setReturnStep(null); setStep(0); }
    } else if (known.name !== tableName) {
      setTableName(known.name);
    }
  }, [tables, tableNumber, tableName, step, setTableNumber, setTableName]);

  useEffect(() => {
    const isTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
    if (isTouch) return;
    if (step === 0 && tableRef.current) {
      setTimeout(() => tableRef.current?.focus(), 300);
    }
    if (step === 1 && otpStage === "phone" && phoneRef.current) {
      setTimeout(() => phoneRef.current?.focus(), 300);
    }
    if (step === 1 && otpStage === "otp" && otpRef.current) {
      setTimeout(() => otpRef.current?.focus(), 300);
    }
  }, [step, otpStage]);

  // If the customer already verified in an earlier session, skip straight
  // past the OTP screen when they reach step 1.
  useEffect(() => {
    if (step === 1 && isAuthenticated && !reauthRequired && !verifyPeek) {
      setStep(2);
    }
  }, [step, isAuthenticated, reauthRequired, verifyPeek]);
  useEffect(() => { if (step !== 1) setVerifyPeek(false); }, [step]);

  // A cart belongs to the account that first saved it. If a different
  // customer verifies on this same device ("Not you?" or a re-verify with
  // another number), drop the old server cart + anything applied to it so
  // their order starts clean instead of hitting someone else's cart.
  const lastUserIdRef = useRef(userId);
  useEffect(() => {
    if (lastUserIdRef.current && userId && lastUserIdRef.current !== userId) {
      setCafeMenuCartId(null);
      setAppliedDiscount(null);
      setAppliedReward(null);
      setCircle(null);
      resetSavedCart();
    }
    if (userId) lastUserIdRef.current = userId;
  }, [userId, setCafeMenuCartId, setAppliedDiscount, setAppliedReward, resetSavedCart]);

  useEffect(() => {
    if (!isAuthenticated) setCircle(null);
  }, [isAuthenticated]);

  // A saved session can look valid client-side while the JWT has actually
  // expired server-side — cafemenuApi detects that on the first failing
  // call and clears it, which flips isAuthenticated to false here. Bounce
  // back to the mobile-number step instead of leaving the customer stuck on
  // a "verified" screen where every rewards/discount/payment call just
  // keeps failing the same way.
  useEffect(() => {
    if (step >= 2 && !isAuthenticated) {
      setOtpStage("phone");
      setOtpMessage("Your session has expired. Please verify your mobile number again.");
      setStep(1);
    }
  }, [step, isAuthenticated]);

  // Save the cart to the backend when the customer reaches the Review step (a reward/offer needs a
  // real cartId to validate against) — and again only if something that is SAVED has changed:
  // the dishes, the table number or the note. Coming back to this page with the same cart (after the
  // Customise sheet, the Rewards page, a step tap…) sends nothing: the "already saved" record lives in
  // CartContext, not here. Edits made while on this step (e.g. + / − taps) are batched into one call.
  useEffect(() => {
    if (step !== 2 || !isAuthenticated) return undefined;
    const itemsKey = cartItemsKey(items, getApiTaxRate);
    // A table is always chosen first now (it's the key info for the barista).
    // The dining choice is tagged onto the instructions the barista sees so a
    // dine-in order is never over-packed, and a takeaway is always packed.
    const saveTableNumber = tableNumber || null;
    const saveTableName = tableName || null;
    const diningTag = orderType === "takeaway" ? "TAKEAWAY" : "DINE-IN";
    const saveInstructions =
      [diningTag, (specialInstructions || "").trim()].filter(Boolean).join(" — ") || null;
    const signature = JSON.stringify([itemsKey, orderType || "", saveTableNumber || "", saveTableName || "", specialInstructions || ""]);
    const saved = getSavedCart();
    if (saved.signature === signature && cafeMenuCartId) return undefined;

    const save = () => {
      if (!claimCartSave(signature)) return;
      // Only a change to the dishes invalidates an applied reward/offer; a note or table edit leaves it.
      const itemsChanged = itemsKey !== getSavedCart().itemsKey;
      setCartSyncError("");
      saveCafeMenuCart(token, {
        cartId: cafeMenuCartId || null,
        branchCode: cafeId,
        items: toCafeMenuApiItems(items, getApiTaxRate),
        tableNumber: saveTableNumber,
        tableName: saveTableName,
        specialInstructions: saveInstructions,
      })
        .then((cart) => {
          if (!finishCartSave(signature, itemsKey, true)) return; // a newer save superseded this one
          setCafeMenuCartId(cart.cartId);
          setServerTotals(totalsOf(cart));
          if (itemsChanged) {
            setAppliedDiscount(null);
            setAppliedReward(null);
          } else {
            // Note/table-only save: follow whatever the server says is still applied.
            if (!cart.appliedRewardId) setAppliedReward(null);
            if (!cart.appliedDiscountId) setAppliedDiscount(null);
          }
        })
        .catch((e) => {
          finishCartSave(signature, itemsKey, false); // allow a retry the next time this step runs
          setCartSyncError(e instanceof CafeMenuApiError ? e.message : "Unable to save your cart. Please try again.");
        });
    };

    // The first save of a visit goes out at once; later edits wait a moment so a burst of taps is one call.
    const timer = setTimeout(save, saved.signature ? 600 : 0);
    return () => clearTimeout(timer);
  }, [step, isAuthenticated, token, items, cafeId, tableNumber, tableName, orderType, specialInstructions, cafeMenuCartId, setCafeMenuCartId, setAppliedDiscount, setAppliedReward, setServerTotals, getApiTaxRate, getSavedCart, claimCartSave, finishCartSave]);

  // Circle membership: asked once per login. The answer is remembered for the visit.
  useEffect(() => {
    if (step !== 2 || !isAuthenticated) return undefined;
    const cacheKey = `circle:${token}`;
    const cached = cacheGet(cacheKey);
    if (cached) {
      setCircle(cached);
      return undefined;
    }
    let cancelled = false;
    setCircleLoading(true);
    setCircleError("");
    getCircleStatus(token)
      .then((data) => {
        if (cancelled) return;
        cacheSet(cacheKey, data, 10 * 60 * 1000);
        setCircle(data);
      })
      .catch((e) => {
        if (!cancelled) setCircleError(e instanceof CafeMenuApiError ? e.message : "Unable to check your Circle status right now.");
      })
      .finally(() => {
        if (!cancelled) setCircleLoading(false);
      });
    return () => { cancelled = true; };
  }, [step, isAuthenticated, token, circleRetry]);

  // Ask Circle again (and, once they are a member, the coins) — bypasses the remembered answers.
  const recheckCircle = () => {
    cacheClear(`circle:${token}`);
    cacheClear(`coins:${token}`);
    setCircleRetry((n) => n + 1);
  };
  // A customer who is eligible but has not joined leaves for the join page. When they are back on this
  // page (tab visible again / page shown again) check once more by themselves — if they joined, the
  // coins load right after. Only listens while that is the situation, so nobody else triggers extra calls.
  const recheckRef = useRef(recheckCircle);
  recheckRef.current = recheckCircle;
  const waitingToJoin = step === 2 && isAuthenticated && Boolean(circle?.canJoin) && !circle?.isMember;
  useEffect(() => {
    if (!waitingToJoin) return undefined;
    let last = 0;
    const onBack = () => {
      if (document.visibilityState === "hidden" || Date.now() - last < 3000) return;
      last = Date.now();
      recheckRef.current();
    };
    document.addEventListener("visibilitychange", onBack);
    window.addEventListener("focus", onBack);
    window.addEventListener("pageshow", onBack);
    return () => {
      document.removeEventListener("visibilitychange", onBack);
      window.removeEventListener("focus", onBack);
      window.removeEventListener("pageshow", onBack);
    };
  }, [waitingToJoin]);

  // Coin balance: only changes when an order is paid, so it is fetched once and remembered.
  useEffect(() => {
    if (step !== 2 || !isAuthenticated || !circle?.isMember) return undefined;
    const cacheKey = `coins:${token}`;
    const cached = cacheGet(cacheKey);
    if (cached !== undefined) {
      setLoyaltyBalance(cached);
      return undefined;
    }
    let cancelled = false;
    setLoyaltyLoading(true);
    setLoyaltyError("");
    getLoyaltyPoints(token)
      .then((balance) => {
        if (cancelled) return;
        cacheSet(cacheKey, balance, 5 * 60 * 1000);
        setLoyaltyBalance(balance);
      })
      .catch((e) => {
        if (cancelled) return;
        setLoyaltyError(e instanceof CafeMenuApiError ? e.message : "Unable to fetch your circle points right now.");
        setLoyaltyBalance(0);
      })
      .finally(() => {
        if (!cancelled) setLoyaltyLoading(false);
      });
    return () => { cancelled = true; };
  }, [step, isAuthenticated, token, circle]);

  // Circle Rewards for THIS cart (eligibility depends on what is in it). Fetched only once the server
  // copy matches the cart on screen, then remembered per cart contents — so it is asked again only
  // when the dishes change, never for a note/table edit, a page revisit or applying a reward.
  const rewardsFetchKeyRef = useRef("");
  useEffect(() => {
    if (step !== 2 || !isAuthenticated || !circle?.isMember || !cafeMenuCartId) return;
    const itemsKey = cartItemsKey(items, getApiTaxRate);
    if (getSavedCart().itemsKey !== itemsKey) return; // wait for the save of these dishes to land
    const cacheKey = `rewards:${cafeMenuCartId}:${itemsKey}`;
    const cached = cacheGet(cacheKey);
    if (cached) {
      setRewardOffers(cached);
      setRewardOffersKey(cacheKey);
      return;
    }
    if (rewardsFetchKeyRef.current === cacheKey) return; // this exact request is already on its way
    rewardsFetchKeyRef.current = cacheKey;
    setRewardOffersLoading(true);
    setRewardOffersError("");
    getCircleRewards(token, cafeMenuCartId)
      .then((list) => {
        cacheSet(cacheKey, list, 5 * 60 * 1000);
        if (rewardsFetchKeyRef.current === cacheKey) {
          setRewardOffers(list);
          setRewardOffersKey(cacheKey);
        }
      })
      .catch((e) => {
        if (rewardsFetchKeyRef.current === cacheKey) {
          setRewardOffersError(e instanceof CafeMenuApiError ? e.message : "Unable to fetch rewards right now.");
        }
      })
      .finally(() => {
        if (rewardsFetchKeyRef.current === cacheKey) {
          rewardsFetchKeyRef.current = "";
          setRewardOffersLoading(false);
        }
      });
  }, [step, isAuthenticated, token, cafeMenuCartId, circle, items, serverTotals, getApiTaxRate, getSavedCart]);

  useEffect(() => {
    const stepName = step === 0 ? "table" : step === 1 ? "phone" : step === 2 ? "rewards" : "summary_ready";
    const key = `${stepName}-${itemCount}-${Boolean(tableNumber)}-${Boolean(phoneNumber)}`;
    if (viewedStepsRef.current.has(key)) return;
    viewedStepsRef.current.add(key);

    trackCartStepViewed({
      cafeId,
      cafeName,
      stepName,
      itemCount,
      lineCount: items.length,
      subtotal,
      taxTotal,
      grandTotal,
      hasTableNumber: Boolean(tableNumber),
      hasPhoneNumber: Boolean(phoneNumber),
    });
  }, [cafeId, cafeName, step, itemCount, items.length, subtotal, taxTotal, grandTotal, tableNumber, phoneNumber]);

  // Tapping a step in the indicator jumps there. Going forward is only allowed as
  // far as what's already done (table entered, number verified).
  const goToStep = (target) => {
    if (target === step) return;
    setShowRewards(false);
    if (target === 0) {
      setReturnStep(step > 0 ? step : null);
      setStep(0);
      return;
    }
    // Step 0 is done when a table is chosen and the dining type is picked.
    const step0Done = tableNumber.trim().length > 0 && Boolean(orderType);
    if (!step0Done) {
      setStep(0);
      if (!tableNumber.trim().length) setTableError(true);
      else setOrderTypeError(true);
      return;
    }
    if (target === 1) {
      if (isAuthenticated) setVerifyPeek(true);
      setReturnStep(step === 2 ? 2 : null);
      setStep(1);
      return;
    }
    // target === 2
    setReturnStep(null);
    setStep(isAuthenticated && !reauthRequired ? 2 : 1);
  };

  const handleTableContinue = () => {
    // Table first — it's the most important thing for the barista.
    if (!tableNumber.trim().length) {
      setTableError(true);
      tableRef.current?.focus();
      return;
    }
    // Then the dine-in / takeaway choice (revealed once a table is set).
    if (!orderType) {
      setOrderTypeError(true);
      return;
    }
    setTableError(false);
    setOrderTypeError(false);
    trackCartTableCompleted({
      cafeId,
      cafeName,
      itemCount,
      lineCount: items.length,
      subtotal,
      taxTotal,
      grandTotal,
      hasTableNumber: Boolean(tableNumber),
      hasPhoneNumber: Boolean(phoneNumber),
    });
    if (returnStep !== null && returnStep > 0) {
      const dest = returnStep;
      setReturnStep(null);
      setStep(dest);
    } else {
      setReturnStep(null);
      setStep(1);
    }
  };

  const handleSendOtp = async () => {
    if (!isValidIndianPhone(phoneNumber)) {
      setPhoneError(true);
      phoneRef.current?.focus();
      return;
    }
    setPhoneError(false);
    setOtpMessage("");
    setOtpSending(true);
    try {
      const tenDigit = phoneNumber.replace(/\D/g, "").slice(-10);
      await sendOtp(tenDigit);
      setOtpStage("otp");
    } catch (e) {
      setOtpMessage(e instanceof CafeMenuApiError ? e.message : "Unable to send OTP. Please try again.");
    } finally {
      setOtpSending(false);
    }
  };

  const completeVerification = () => {
    trackCartPhoneCompleted({
      cafeId,
      cafeName,
      itemCount,
      lineCount: items.length,
      subtotal,
      taxTotal,
      grandTotal,
      hasTableNumber: Boolean(tableNumber),
      hasPhoneNumber: true,
    });
    trackCartPhoneCaptured({
      cafeId,
      cafeName,
      itemCount,
      lineCount: items.length,
      subtotal,
      taxTotal,
      grandTotal,
      hasTableNumber: Boolean(tableNumber),
      hasPhoneNumber: true,
    });
    setStep(2);
  };

  const handleVerifyOtp = async () => {
    if (otpCode.trim().length < 4) {
      setOtpMessage("Please enter the OTP sent to your number.");
      return;
    }
    setOtpMessage("");
    setOtpVerifying(true);
    try {
      const tenDigit = phoneNumber.replace(/\D/g, "").slice(-10);
      await verifyOtp(tenDigit, otpCode.trim());
      setMultiDeviceConfirm(null);
      completeVerification();
    } catch (e) {
      setOtpMessage(e instanceof CafeMenuApiError ? e.message : "Invalid or expired OTP. Please try again.");
    } finally {
      setOtpVerifying(false);
    }
  };

  // The backend only debits coins once the order is paid (applying a reward
  // just reserves it), so show the balance net of the reward on this cart.
  const reservedCoins = Number(appliedReward?.cost) || 0;
  const availableCoins = Math.max(0, (Number(loyaltyBalance) || 0) - reservedCoins);

  // What will actually be charged: the server's figure (reward/offer off the
  // item prices first, GST on what is left); the plain cart total until the
  // first save comes back.
  const hasServerTotals = Boolean(serverTotals && serverTotals.cartId === cafeMenuCartId);
  const payableNow = hasServerTotals ? formatRupees(serverTotals.totalPrice) : Math.round(grandTotal);
  const billItemsTotal = hasServerTotals ? serverTotals.itemTotal : subtotal;
  const billGst = hasServerTotals ? serverTotals.taxAmount : taxTotal;
  const billReward = appliedReward?.amount > 0 ? appliedReward.amount : 0;
  const billDiscount = appliedDiscount?.amount > 0 ? appliedDiscount.amount : 0;

  // The Rewards page only makes sense for a verified customer on the cart's review step.
  useEffect(() => {
    if (showRewards && (!isAuthenticated || step !== 2)) setShowRewards(false);
  }, [showRewards, isAuthenticated, step]);

  // What the join sheet may need to know about this account (memoised: the sheet restarts if it changes).
  const joinAccount = useMemo(
    () => ({ phone: authPhoneNumber, name: circle?.name, email: circle?.email }),
    [authPhoneNumber, circle?.name, circle?.email]
  );

  // Joining Circle changes the account and grants a bonus, so — like spending coins — it needs a recent OTP.
  const handleJoinCircle = () => {
    if (!isRecentlyVerified()) {
      requestReauth();
      setOtpStage("phone");
      setOtpCode("");
      setPhoneNumber((authPhoneNumber || "").replace(/\D/g, "").slice(-10));
      setReauthFor("join");
      setOtpMessage("For your security, please verify your number again to join Circle.");
      setStep(1);
      return;
    }
    setShowJoin(true);
  };

  const handleSelectReward = async (offer) => {
    if (!cafeMenuCartId) {
      setRewardApplyError("Please wait for your cart to finish saving, then try again.");
      return;
    }
    setRewardApplyError("");
    if (appliedReward?.id === offer.id) {
      setRewardApplyingId(offer.id);
      try {
        const cleared = await removeCafeMenuReward(token, cafeMenuCartId);
        setAppliedReward(null);
        if (cleared) setServerTotals(totalsOf(cleared));
      } catch (e) {
        setRewardApplyError(e instanceof CafeMenuApiError ? e.message : "Unable to remove this reward.");
      } finally {
        setRewardApplyingId(null);
      }
      return;
    }
    if (offer.active === false) {
      setRewardApplyError(offer.reason || "This reward isn't available right now.");
      return;
    }
    // Spending Circle points needs a recent OTP, not just a still-valid
    // login from earlier in the visit.
    if (!isRecentlyVerified()) {
      setShowRewards(false);
      requestReauth();
      setOtpStage("phone");
      setOtpCode("");
      setPhoneNumber((authPhoneNumber || "").replace(/\D/g, "").slice(-10));
      setReauthFor("rewards");
      setOtpMessage("For your security, please verify your number again to use Circle rewards.");
      setStep(1);
      return;
    }
    setRewardApplyingId(offer.id);
    try {
      const cart = await applyCafeMenuReward(token, cafeMenuCartId, Number(offer.id));
      setAppliedReward({ id: cart.appliedRewardId, name: cart.appliedRewardName, cost: cart.appliedRewardCost, amount: cart.appliedRewardDiscountAmount });
      setServerTotals(totalsOf(cart));
      setAppliedDiscount(null);
      // Back to the cart, which now shows the bill with the reward taken off.
      setShowRewards(false);
      setBillOpen(true);
    } catch (e) {
      setRewardApplyError(e instanceof CafeMenuApiError ? e.message : "Unable to apply this reward.");
    } finally {
      setRewardApplyingId(null);
    }
  };

  // Pay straight from the cart: Razorpay opens right away (or the order is placed
  // directly when points / an offer cover the whole bill).
  const handlePayFromCart = () => {
    if (checkout.paying) return;
    checkout.pay();
  };

  const handleRewardsContinue = () => {
    if (returnStep !== null && returnStep > 2) {
      const dest = returnStep;
      setReturnStep(null);
      setStep(dest);
    } else {
      setReturnStep(null);
      onViewSummary();
    }
  };

  const stepNames = ["table", "verify", "rewards", "summary_ready"];
  const handleBack = () => {
    trackCartAbandoned({
      cafeId,
      cafeName,
      abandonStep: stepNames[step] || "unknown",
      itemCount,
      lineCount: items.length,
      subtotal,
      taxTotal,
      grandTotal,
      hasTableNumber: Boolean(tableNumber),
      hasPhoneNumber: Boolean(phoneNumber),
    });
    onBack();
  };

  const stepLabel = (idx, label, done) => (
    <button type="button" className={`cart-step-dot ${step === idx ? "active" : ""} ${done ? "done" : ""}`} onClick={() => goToStep(idx)} aria-current={step === idx ? "step" : undefined} aria-label={`${label}${done ? " (done)" : ""}`}>
      {done ? (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M3 7.5l2.5 2.5 5.5-5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <span className="cart-step-num">{idx + 1}</span>
      )}
      <span className="cart-step-label">{label}</span>
    </button>
  );

  return (
    <>
      <div className="cart-page-backdrop" onClick={handleBack} />
      <div className={`cart-page billing-ui ${keyboardOpen ? "keyboard-open" : ""} ${noteEditing ? "note-editing" : ""}`} ref={cartPageRef}>
        <div className="cart-page-header">
          <button className="cart-page-back" onClick={handleBack} aria-label="Back to menu">
            ←
          </button>
          <h1>Your Cart</h1>
          <span className="cart-page-count">{itemCount} item{itemCount === 1 ? "" : "s"}</span>
        </div>

        {items.length > 0 && !(isAuthenticated && step >= 2) && (
          <div className="cart-page-total-bar">
            <div className="cart-total-info">
              <span className="cart-total-label">Total payable</span>
              <span className="cart-total-breakdown">
                Subtotal ₹{Math.round(subtotal)}
                {(hasServerTotals ? serverTotals.taxAmount : taxTotal) > 0
                  ? ` · GST ₹${formatRupees(hasServerTotals ? serverTotals.taxAmount : taxTotal)}`
                  : ""}
              </span>
            </div>
            <strong className="cart-total-amount">₹{payableNow}</strong>
          </div>
        )}

        <div className="cart-page-items">
          {items.length === 0 ? (
            <div className="cart-empty">
              <div className="cart-empty-icon">☕</div>
              <p>Your cart is empty</p>
              <button className="cart-empty-btn" onClick={handleBack}>
                Browse Menu
              </button>
            </div>
          ) : (
            <>
            {items.map((item) => (
              <div className="cart-row" key={item.id}>
                {item.imageUrl && (
                  <img className="cart-row-img" src={item.imageUrl} alt={item.name} />
                )}
                <div className="cart-row-main">
                  <div className="cart-row-top">
                    <div className="cart-row-info">
                      <p className="cart-row-name">
                        {renderDietaryTags(item)}
                        <span className="cart-row-name-text">{item.name}</span>
                      </p>
                      {(item.variantTitle || item.selectedOptions?.length > 0) && (
                        <p className="cart-row-options">
                          {[
                            item.variantTitle,
                            ...(item.selectedOptions || []).map((o) =>
                              Number(o?.price) > 0 ? `${o.title} (+₹${Math.round(o.price)})` : o.title
                            ),
                          ].filter(Boolean).join(", ")}
                        </p>
                      )}
                      {onEditItem && (item.variantTitle || item.selectedOptions?.length > 0) && (
                        <button
                          className="cart-row-edit"
                          onClick={() => { onBack(); onEditItem(item); }}
                        >
                          <EditIcon /> Customise
                        </button>
                      )}
                    </div>
                    <button
                      className="cart-row-delete"
                      onClick={() => removeItem(item.id)}
                      aria-label={`Remove ${item.name} from cart`}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M4 7h16M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2m2 0v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7h14ZM10 11v6M14 11v6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                  <div className="cart-row-bottom">
                    <div className="cart-qty-controls">
                      <button
                        className="cart-qty-btn"
                        onClick={() => updateQuantity(item.id, -1)}
                        aria-label={
                          item.quantity <= 1 ? `Remove ${item.name}` : `Remove one ${item.name}`
                        }
                      >
                        −
                      </button>
                      <span className="cart-qty-value">{item.quantity}</span>
                      <button
                        className="cart-qty-btn"
                        onClick={() => updateQuantity(item.id, 1)}
                        aria-label={`Add one ${item.name}`}
                      >
                        +
                      </button>
                    </div>
                    <strong className="cart-row-line">₹{Math.round(item.unitPrice * item.quantity)}</strong>
                  </div>
                </div>
              </div>
            ))}
            <OrderNote
              value={specialInstructions}
              onCommit={setSpecialInstructions}
              onEditingChange={setNoteEditing}
            />
            </>
          )}
        </div>

        {items.length > 0 && (
          <div className="cart-page-footer">
            <div className="cart-steps-indicator">
              {stepLabel(0, "Order", step > 0)}
              <div className={`cart-step-line ${step > 0 ? "done" : ""}`} />
              {stepLabel(1, "Verify", step > 1)}
              <div className={`cart-step-line ${step > 1 ? "done" : ""}`} />
              {stepLabel(2, "Review", false)}
            </div>

            <div className="cart-step-content" key={step}>
              {step === 0 && (
                <div className="cart-step-panel">
                  {/* Table is the most important thing, so it comes first. The
                      dine-in / takeaway choice appears only once a table is set. */}
                  <p className="cart-step-title">{manualTable ? "Your table number" : "Select your table"}</p>
                  <p className="cart-step-hint">so the barista can find your order</p>

                  {!manualTable && (
                    <TablePicker
                      status={tables.status}
                      tables={tables.list}
                      value={tableNumber}
                      invalid={tableError}
                      onSelect={(table) => { setTableNumber(table.number); setTableName(table.name); if (tableError) setTableError(false); }}
                      onRetry={() => { cacheClear("tables:"); setTablesAttempt((n) => n + 1); }}
                    />
                  )}
                  {manualTable && (
                  <div className={`cart-step-input-row ${tableError ? "error" : ""}`}>
                    <span className="cart-step-prefix">#</span>
                    <input
                      ref={tableRef}
                      className="cart-step-input cart-step-input-table"
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder="e.g. 12"
                      value={tableNumber}
                      onChange={(e) => {
                        setTableNumber(e.target.value.replace(/[^\d]/g, "").slice(0, 2));
                        setTableName("");
                        if (tableError) setTableError(false);
                      }}
                      onKeyDown={(e) => e.key === "Enter" && handleTableContinue()}
                      maxLength={2}
                    />
                  </div>
                  )}
                  {tableError && (
                    <p className="cart-step-error">{manualTable ? "Please enter your table number" : "Please select your table"}</p>
                  )}

                  {/* Only once a table is chosen: a compact dine-in / takeaway toggle. */}
                  {tableNumber.trim().length > 0 && (
                    <div className="dining-section">
                      <p className="cart-step-subtitle">How are you having it?</p>
                      <div className={`dining-choice dining-choice-compact ${orderTypeError ? "error" : ""}`} role="radiogroup" aria-label="Dine-in or takeaway">
                        <button
                          type="button"
                          role="radio"
                          aria-checked={orderType === "dinein"}
                          className={`dining-chip ${orderType === "dinein" ? "selected" : ""}`}
                          onClick={() => { setOrderType("dinein"); setOrderTypeError(false); }}
                        >
                          <span className="dining-chip-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M4 3v7a2 2 0 0 0 2 2h0a2 2 0 0 0 2-2V3M6 12v9M18 3c-1.7 0-3 2-3 5s1.3 4 3 4v9" />
                            </svg>
                          </span>
                          <span className="dining-chip-label">Dine-in</span>
                        </button>

                        <button
                          type="button"
                          role="radio"
                          aria-checked={orderType === "takeaway"}
                          className={`dining-chip ${orderType === "takeaway" ? "selected" : ""}`}
                          onClick={() => { setOrderType("takeaway"); setOrderTypeError(false); }}
                        >
                          <span className="dining-chip-icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M6 8h12l-1 11a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L6 8zM9 8V6a3 3 0 0 1 6 0v2M9 12h6" />
                            </svg>
                          </span>
                          <span className="dining-chip-label">Takeaway</span>
                        </button>
                      </div>
                      {orderTypeError && (
                        <p className="cart-step-error">Please choose dine-in or takeaway</p>
                      )}
                      {orderType === "takeaway" && (
                        <p className="cart-step-hint dining-chip-note">We'll pack your order to go.</p>
                      )}
                    </div>
                  )}

                  <button className="cart-step-btn-primary" onClick={handleTableContinue}>
                    {returnStep !== null ? "Save" : "Continue"}
                  </button>
                </div>
              )}

              {step === 1 && isAuthenticated && verifyPeek && (
                <div className="cart-step-panel">
                  <p className="cart-step-title">Verified</p>
                  <p className="cart-step-identity">+91 {(authPhoneNumber || "").replace(/\D/g, "").slice(-10)}</p>
                  <button className="cart-step-btn-primary" onClick={() => { setVerifyPeek(false); setStep(2); }}>
                    Continue
                  </button>
                  <button
                    type="button"
                    className="cart-step-text-action"
                    onClick={() => {
                      logout();
                      setCafeMenuCartId(null);
                      setAppliedDiscount(null);
                      setAppliedReward(null);
                      resetSavedCart();
                      setPhoneNumber("");
                      setOtpStage("phone");
                      setOtpCode("");
                      setOtpMessage("");
                      setVerifyPeek(false);
                    }}
                  >
                    Use a different number
                  </button>
                </div>
              )}

              {step === 1 && !(isAuthenticated && verifyPeek) && (
                <div className="cart-step-panel">
                  <div className="cart-reward-card">
                    <div className="cart-reward-icon">
                      <CoinIcon size={42} />
                    </div>
                    <div className="cart-reward-text">
                      <p className="cart-reward-title">Enter your mobile number</p>
                      <p className="cart-reward-sub">to complete the order.</p>
                    </div>
                  </div>

                  {otpStage === "phone" ? (
                    <>
                      <div className={`cart-step-input-row ${phoneError ? "error" : ""}`}>
                        <span className="cart-step-prefix">+91</span>
                        <input
                          ref={phoneRef}
                          className="cart-step-input"
                          type="tel"
                          inputMode="numeric"
                          autoComplete="tel-national"
                          enterKeyHint="send"
                          aria-label="Mobile number"
                          placeholder="10-digit mobile number"
                          value={phoneNumber}
                          onChange={(e) => {
                            setPhoneNumber(e.target.value.replace(/[^\d]/g, "").slice(0, 10));
                            if (phoneError) setPhoneError(false);
                          }}
                          onKeyDown={(e) => e.key === "Enter" && handleSendOtp()}
                          maxLength={10}
                        />
                      </div>
                      {phoneError && (
                        <p className="cart-step-error">Please enter a valid 10-digit number</p>
                      )}
                      {otpMessage && <p className="cart-step-error">{otpMessage}</p>}
                      <button className="cart-step-btn-primary" onClick={handleSendOtp} disabled={otpSending}>
                        {otpSending ? "Sending OTP…" : "Send OTP"}
                      </button>
                      {reauthRequired && isAuthenticated && (
                        <button
                          type="button"
                          className="cart-step-text-action"
                          onClick={() => {
                            cancelReauth();
                            setOtpMessage("");
                            setStep(2);
                          }}
                        >
                          {reauthFor === "join" ? "Continue without joining" : "Continue without using rewards"}
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <button className="cart-step-done-tag" onClick={() => { setOtpStage("phone"); setOtpMessage(""); }} aria-label="Edit phone number">
                        <span className="cart-step-done-tag-text">+91 {phoneNumber}</span>
                        <span className="cart-step-done-tag-edit"><EditIcon /> Edit</span>
                      </button>
                      <p className="cart-step-title">Enter the OTP</p>
                      <p className="cart-step-hint">Sent via SMS to your number</p>
                      <div className="cart-step-input-row">
                        <input
                          ref={otpRef}
                          className="cart-step-input"
                          type="tel"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          enterKeyHint="go"
                          aria-label="One-time password"
                          placeholder="6-digit OTP"
                          value={otpCode}
                          onChange={(e) => setOtpCode(e.target.value.replace(/[^\d]/g, "").slice(0, 6))}
                          onKeyDown={(e) => e.key === "Enter" && handleVerifyOtp()}
                          maxLength={6}
                        />
                      </div>
                      {otpMessage && <p className="cart-step-error">{otpMessage}</p>}
                      <button className="cart-step-btn-primary" onClick={handleVerifyOtp} disabled={otpVerifying}>
                        {otpVerifying ? "Verifying…" : "Verify & Continue"}
                      </button>
                      <div className="cart-step-resend-row">
                        <button className="cart-step-text-action" onClick={handleSendOtp} disabled={otpSending}>
                          {otpSending ? "Resending…" : "Resend OTP"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {step === 2 && (
                <div className="cart-step-panel">
                  {authPhoneNumber && (
                    <p className="cart-step-identity">
                      Verified +91 {(authPhoneNumber || "").replace(/\D/g, "").slice(-10)}
                      {" · "}
                      <button
                        type="button"
                        className="cart-step-text-action"
                        onClick={() => {
                          logout();
                          setCafeMenuCartId(null);
                          setAppliedDiscount(null);
                          setAppliedReward(null);
                          resetSavedCart();
                          setPhoneNumber("");
                          setOtpStage("phone");
                          setOtpCode("");
                          setOtpMessage("");
                          setStep(1);
                        }}
                      >
                        Not you?
                      </button>
                    </p>
                  )}
                  {circle?.isMember ? (
                    <div className="cart-account-row">
                      <div className="cart-account-coins">
                        <CoinIcon size={24} />
                        <strong>{loyaltyLoading ? "…" : availableCoins}</strong>
                        <span>Circle Coins</span>
                      </div>
                      <button type="button" className={`cart-account-rewards ${appliedReward ? "has-reward" : ""}`} onClick={() => setShowRewards(true)} disabled={checkout.paying}>
                        <OfferIcon />
                        {appliedReward ? `${appliedReward.name} applied` : "Rewards"}
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      </button>
                    </div>
                  ) : (
                    <CircleStatusRow
                      circle={circle}
                      loading={circleLoading}
                      error={circleError}
                      onRetry={recheckCircle}
                      onRefresh={recheckCircle}
                      onJoin={handleJoinCircle}
                      emailVerified={circle?.emailVerified === true}
                      firstName={firstNameOf(circle?.name || authName)}
                    />
                  )}

                  {cartSyncError && <p className="cart-step-error">{cartSyncError}</p>}
                  {cartSyncError.includes("already been placed") && (
                    <button
                      className="cart-step-btn-primary"
                      onClick={() => {
                        clearCart({ clearSource: "order_already_placed" });
                      }}
                    >
                      Start a new order
                    </button>
                  )}

                  <div className={`cart-bill ${billOpen ? "open" : ""}`}>
                    <button type="button" className="cart-bill-toggle" onClick={() => setBillOpen((v) => !v)} aria-expanded={billOpen}>
                      <span className="cart-bill-title">Bill Summary</span>
                      {(billReward > 0 || billDiscount > 0) && !billOpen && (
                        <span className="cart-bill-chip">You save ₹{formatRupees(billReward + billDiscount)}</span>
                      )}
                      <span className="cart-bill-toggle-total">₹{payableNow}</span>
                      <svg className="cart-bill-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </button>
                    {billOpen && (
                      <div className="cart-bill-details">
                        <div className="cart-bill-row">
                          <span>Items Total</span>
                          <span>₹{formatRupees(billItemsTotal)}</span>
                        </div>
                        {billReward > 0 && (
                          <div className="cart-bill-row cart-bill-saving">
                            <span>{appliedReward?.name || "Reward"} applied</span>
                            <span>−₹{formatRupees(billReward)}</span>
                          </div>
                        )}
                        {billDiscount > 0 && (
                          <div className="cart-bill-row cart-bill-saving">
                            <span>{appliedDiscount?.name || "Offer"} applied</span>
                            <span>−₹{formatRupees(billDiscount)}</span>
                          </div>
                        )}
                        <div className="cart-bill-row cart-bill-muted">
                          <span>GST</span>
                          <span>+₹{formatRupees(billGst)}</span>
                        </div>
                        <div className="cart-bill-total">
                          <span>Total Amount</span>
                          <strong>₹{payableNow}</strong>
                        </div>
                      </div>
                    )}
                  </div>

                  {checkout.payError && <p className="cart-step-error cart-pay-error">{checkout.payError}</p>}
                  <button className="cart-step-btn-primary cart-pay-btn" onClick={handlePayFromCart} disabled={!hasServerTotals || checkout.paying}>
                    {!hasServerTotals ? "Loading…" : Number(payableNow) === 0 ? "Place order" : `Pay ₹${payableNow}`}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {checkout.paying && <PayingOverlay stage={checkout.payStage} showHighTrafficNotice={checkout.showHighTrafficNotice} />}

      {checkout.paymentFailedReason && (
        <PaymentIssueScreen reason={checkout.paymentFailedReason} onTryAgain={checkout.clearPaymentFailed} />
      )}

      {checkout.orderResult && !checkout.paymentFailedReason && (
        <OrderPlacedScreen
          orderResult={checkout.orderResult}
          cafeName={cafeName}
          tableNumber={tableNumber}
          orderType={orderType}
          items={items}
          itemsTotal={billItemsTotal}
          rewardName={appliedReward?.name}
          rewardAmount={billReward}
          discountName={appliedDiscount?.name}
          discountAmount={billDiscount}
          gst={billGst}
          amount={hasServerTotals ? serverTotals.totalPrice : 0}
          isFree={hasServerTotals && Number(serverTotals.totalPrice) === 0}
          onDone={() => {
            clearCart({ clearSource: "order_summary_done" });
            checkout.clearOrderResult();
            if (onOrderDone) onOrderDone();
            else onBack();
          }}
        />
      )}

      {showRewards && (
        <RewardsPage
          onClose={() => setShowRewards(false)}
          circle={circle}
          circleLoading={circleLoading}
          circleError={circleError}
          onRetryCircle={() => {
            cacheClear(`circle:${token}`);
            setCircleRetry((n) => n + 1);
          }}
          coins={availableCoins}
          coinsLoading={loyaltyLoading}
          coinsError={loyaltyError}
          reservedCoins={reservedCoins}
          appliedReward={appliedReward}
          offers={rewardOffers}
          offersLoading={
            rewardOffersLoading ||
            // Member whose rewards for THIS cart haven't landed yet (cart still
            // saving, or request in flight): keep showing the loader rather than
            // flashing "No rewards available".
            Boolean(
              isAuthenticated &&
                circle?.isMember &&
                cafeMenuCartId &&
                !rewardOffersError &&
                rewardOffersKey !== `rewards:${cafeMenuCartId}:${cartItemsKey(items, getApiTaxRate)}`
            )
          }
          offersError={rewardOffersError}
          applyError={rewardApplyError}
          applyingId={rewardApplyingId}
          onSelect={handleSelectReward}
        />
      )}

      {showJoin && isAuthenticated && (
        <JoinCircleSheet
          fallbackUrl={circle?.joinUrl}
          account={joinAccount}
          onJoined={recheckCircle}
          onClose={() => setShowJoin(false)}
        />
      )}
    </>
  );
}
